import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Button, Grid, Typography } from "@mui/material";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import LoadingButton from "../../../../components/Buttons/LoadingButton";
import Modal from "../../../../components/Modal";
import { useTranslation } from "react-i18next";
import { statusTypes, trainingLocationTypes } from "../../../../constants";
import {
  phoneNumberValidation,
  validateZipCode
} from "../../../../utils/validation";
import TextField from "../../../../components/FormikFields/TextField";
import SwitchField from "../../../../components/FormikFields/SwitchField";
import ConfirmLocationModal from "./ConfirmLocationModal";
import PhoneNumberInput from "../../../../components/FormikFields/PhoneNumberInput";
import useScopedTranslation from "../../../../hooks/useScopedTranslation";
import StateSelector from "../../../../components/FormikFields/StateSelector";

const getSchema = (t, currentTrainingLocationType) => {
  // Determine if field is required based on if training location type is internal or external
  const fieldRequired = (
    field,
    trainingLocationType = trainingLocationTypes.external
  ) =>
    Yup.string().test(
      "trainingLocationType",
      t("validationMessages.fieldRequired"),
      function() {
        return (
          !!this.parent[field] ||
          trainingLocationType !== currentTrainingLocationType
        );
      }
    );

  const setLimit = (field, fieldName, limit) =>
    field.max(
      limit,
      t("validationMessages.cannotExceedLength", {
        type: t(`manageCourses.${fieldName}`),
        maxLength: limit
      })
    );

  return Yup.object().shape({
    name: setLimit(fieldRequired("name"), "locationName", 250),
    address1: setLimit(fieldRequired("address1"), "address1", 100),
    address2: setLimit(Yup.string().nullable(true), "address2", 100),
    city: setLimit(fieldRequired("city"), "city", 40),
    state: fieldRequired("state").nullable(),
    zip: fieldRequired("zip").test(
      "validZipCode",
      t("validationMessages.validZipCode"),
      function() {
        return validateZipCode(this.parent.zip);
      }
    ),
    phone: phoneNumberValidation(t),
    seats: Yup.number()
      .required(t("validationMessages.fieldRequired"))
      .min(1, t("validationMessages.greaterThanZero"))
      .max(9999, t("validationMessages.lessThanLimit", { limit: "9,999" }))
  });
};

const TrainingLocationForm = ({
  trainingLocationType,
  shouldReset,
  isSaving,
  statusDisabledTooltipMessage
}) => {
  const { resetForm, setSubmitting } = useFormikContext();

  const t = useScopedTranslation("manageCourses");

  const isInternal = trainingLocationType === trainingLocationTypes.internal;

  useEffect(() => {
    resetForm();
  }, [shouldReset]);

  useEffect(() => {
    setSubmitting(isSaving);
  }, [isSaving]);

  const tooltipMessage = isInternal ? t("internalLocationTooltip") : "";

  return (
    <Form>
      <Grid container spacing={4} sx={{ mt: -0.5 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t("locationName")}
            name={"name"}
            disabled={isInternal}
            tooltip={tooltipMessage}
            inputProps={{
              "data-qa": "add-new-training-location-name-field"
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t("address1")}
            name={"address1"}
            disabled={isInternal}
            tooltip={tooltipMessage}
            inputProps={{
              "data-qa": "add-new-training-location-address1-field"
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t("address2")}
            name="address2"
            disabled={isInternal}
            tooltip={tooltipMessage}
            inputProps={{
              "data-qa": "add-new-training-location-address2-field"
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t("city")}
            name="city"
            disabled={isInternal}
            tooltip={tooltipMessage}
            inputProps={{
              "data-qa": "add-new-training-location-city-field"
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StateSelector
            disabled={isInternal}
            disableUnderline={false}
            tooltip={tooltipMessage}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t("zip")}
            name="zip"
            disabled={isInternal}
            tooltip={tooltipMessage}
            inputProps={{
              "data-qa": "add-new-training-location-zip-field"
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <PhoneNumberInput
            name="phone"
            label={t("phone")}
            inputProps={{
              "data-qa": "add-new-training-location-phone-field"
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            type="number"
            label={t("seats")}
            name="seats"
            inputProps={{
              "data-qa": "add-new-training-location-seats-field"
            }}
          />
        </Grid>
        <Grid item xs={4} />
        <Grid item xs={12} sm={4}>
          <SwitchField
            name="statusType.id"
            checkedValue={statusTypes.active}
            uncheckedValue={statusTypes.inactive}
            onLabel={t("locationStatus.active")}
            offLabel={t("locationStatus.inactive")}
            label={t("locationStatus.label")}
            disabled={!!statusDisabledTooltipMessage}
            tooltip={statusDisabledTooltipMessage}
          />
        </Grid>
        <Grid item xs={12} sx={{ mt: 3 }}>
          {isInternal && (
            <Typography variant="body2" color="textSecondary">
              {`*${t("editInternalMsg")}`}.
            </Typography>
          )}
        </Grid>
      </Grid>
    </Form>
  );
};

const EditTrainingLocationModal = ({
  onClose,
  open,
  onSave,
  isSaving,
  trainingLocation
}) => {
  const { t } = useTranslation();
  const trainingLocationType =
    !!trainingLocation && !!trainingLocation.location
      ? trainingLocationTypes.internal
      : trainingLocationTypes.external;
  const [confirmAddressModalOpen, setConfirmAddressModalOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setConfirmAddressModalOpen(false);
    }
  }, [open]);

  const belongsToInactiveLocation =
    !!trainingLocation?.location &&
    trainingLocation?.location?.statusType?.id === statusTypes.inactive;

  return (
    <>
      <Formik
        initialValues={trainingLocation || {}}
        validationSchema={getSchema(t, trainingLocationType)}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          if (
            trainingLocationType === trainingLocationTypes.external &&
            trainingLocation.zip !== values.zip
          ) {
            setConfirmAddressModalOpen(true);
            setSubmitting(false);
          } else {
            onSave(values);
          }
        }}
      >
        {({ submitForm, values, resetForm }) => (
          <>
            <Modal
              scrollable
              maxWidth="md"
              fullWidth
              fillHeight
              dialogTitle={
                <Typography variant="h6" color="textPrimary">
                  {t("manageCourses.editTrainingLocation", {
                    context: trainingLocationType
                  })}
                </Typography>
              }
              dialogContent={
                <TrainingLocationForm
                  trainingLocationType={trainingLocationType}
                  shouldReset={open}
                  isSaving={isSaving}
                  statusDisabledTooltipMessage={
                    belongsToInactiveLocation
                      ? t("manageCourses.inactiveLocationTooltipMessage")
                      : ""
                  }
                />
              }
              dialogActions={
                <>
                  <Button
                    color="primary"
                    onClick={() => {
                      onClose();
                      resetForm();
                    }}
                  >
                    {t("cancelBtn")}
                  </Button>
                  <LoadingButton
                    pending={isSaving}
                    color="primary"
                    variant="contained"
                    onClick={submitForm}
                    disabled={isSaving}
                    data-qa="save-new-training-location-btn"
                  >
                    {t("saveBtn")}
                  </LoadingButton>
                </>
              }
              open={open}
              onClose={onClose}
              showCloseIcon
              data-qa="add-new-training-location-modal"
            />
            <ConfirmLocationModal
              open={confirmAddressModalOpen}
              onConfirm={coordinates => onSave({ ...values, ...coordinates })}
              onClose={() => setConfirmAddressModalOpen(false)}
              location={values}
            />
          </>
        )}
      </Formik>
    </>
  );
};

EditTrainingLocationModal.propTypes = {
  /**
   * Called to close the modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the form is submitted to create a new training location
   */
  onSave: PropTypes.func,
  isSaving: PropTypes.bool,
  trainingLocation: PropTypes.shape({
    name: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    phone: PropTypes.string,
    latitude: PropTypes.string,
    longitude: PropTypes.string,
    locationId: PropTypes.number,
    seats: PropTypes.number
  })
};

export default EditTrainingLocationModal;
